import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { withIdentityPoolId } from "@aws/amazon-location-utilities-auth-helper/dist/amazonLocationAuthHelper";
import Supercluster from "supercluster";

const identityPoolId = "us-east-2:1ad463b2-bdaa-46ff-90e0-2267bbbf7a1d";
const mapName = "golfmap-v1";
const region = "us-east-2";

// Add this new Tooltip component for marker hover
function MarkerTooltip({ tooltipInfo, isMobile, onTitleClick }) {
  if (!tooltipInfo) return null;

  const { course, position } = tooltipInfo;

  const formatYear = (year) => {
    if (!year || year === 'N/D' || year === '') return 'Year Unknown';
    return year;
  };

  const formatHoles = (holes) => {
    if (!holes) return 'Holes Unknown';
    return holes >= 18 ? '18 Holes' : `${holes} Holes`;
  };

  const formatArchitect = (architect) => {
    if (!architect || architect === 'N/D' || architect === '') return null;
    return architect;
  };

  const rankedText = course.top100_state_rank && course.top100_state_rank !== '' 
    ? `#${course.top100_state_rank} in ${course.state}` 
    : null;
    
  const nationalRankedText = course.GD100all_rank && course.GD100all_rank !== '' 
    ? `#${course.GD100all_rank} in America` 
    : null;
    
  const publicRankedText = course.GD100pub_rank && course.GD100pub_rank !== '' 
    ? `#${course.GD100pub_rank} Public in America` 
    : null;

  const titleStyles = { 
    fontWeight: '700', 
    color: '#222', 
    fontSize: '14px',
    marginBottom: '4px',
    wordBreak: 'break-word'
  };

  const linkStyles = {
    ...titleStyles,
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    color: '#1D665D',
    textDecoration: 'underline',
    cursor: 'pointer',
    textAlign: 'left'
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%) translateY(-10px)',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        maxWidth: '280px',
        minWidth: '200px',
        pointerEvents: 'auto',
        fontSize: '13px',
        lineHeight: '1.4'
      }}
    >
      {/* Arrow pointing down to marker */}
      <div style={{
        position: 'absolute',
        bottom: '-6px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid white',
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
      }} />
      
      {/* First Row: Course Name (clickable on mobile) */}
      {isMobile ? (
        <button onClick={() => onTitleClick(course)} style={linkStyles}>
          {course.name}
        </button>
      ) : (
        <div style={{...titleStyles, cursor: 'pointer'}} onClick={() => onTitleClick(course)}>
          {course.name}
        </div>
      )}
      
      {/* Second Row: Club Name */}
      <div style={{ 
        color: '#666', 
        fontSize: '13px',
        marginBottom: '6px',
        fontStyle: 'italic'
      }}>
        {course.club_name}
      </div>
      
      {/* Third Row: Rankings (if applicable) */}
      {(rankedText || nationalRankedText || publicRankedText) && (
        <div style={{ 
          color: '#1D665D', 
          fontSize: '12px',
          fontWeight: '600',
          marginBottom: '4px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          alignItems: 'center'
        }}>
          {nationalRankedText && <span>{nationalRankedText}</span>}
          {publicRankedText && (
            <>
              {nationalRankedText && <span>•</span>}
              <span>{publicRankedText}</span>
            </>
          )}
          {rankedText && (
            <>
              {(nationalRankedText || publicRankedText) && <span>•</span>}
              <span>{rankedText}</span>
            </>
          )}
        </div>
      )}
      
      {/* Fourth Row: Details */}
      <div style={{ 
        color: '#555', 
        fontSize: '12px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <span>{course.type}</span>
        <span>•</span>
        <span>{formatYear(course.open_date)}</span>
        <span>•</span>
        <span>{formatHoles(course.number_of_holes)}</span>
        {formatArchitect(course.course_architect) && (
          <>
            <span>•</span>
            <span>{formatArchitect(course.course_architect)}</span>
          </>
        )}
      </div>
    </div>
  );
}

function SpecializedMap({
  golfCourses,
  selectedCourse,
  setSelectedCourse,
  showSidebar = false,
  sidebarCourses = [],
  renderSidebarCard,
  onVisibleCoursesChange,
  selectedTypes = [],
  selectedArchitects = [],
  minPrice = null,
  maxPrice = null,
  rankedOnly = false,
  sortOrder = 'distance',
  mediaList = [],
  hasMediaOnly = false,
  selectedMediaTypes = [],
  customCriteria = {},
  top100AllOnly = false,
  top100PublicOnly = false,
}) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const isMobile = window.innerWidth <= 768;
  const [hoveredCourseId, setHoveredCourseId] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const justClickedRef = useRef(false);

  // Tooltip state
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  // Clustering state
  const [clusters, setClusters] = useState([]);
  const superclusterRef = useRef(null);
  const MAX_CLUSTER_COURSES = 500;

  // Memoize filtered courses to ensure stable reference
  const filteredCourses = useMemo(() => {
    return golfCourses.filter(course => {
      // Type filter
      const matchesType = selectedTypes.length === 0
        ? false // Show none if nothing is selected
        : (
            (selectedTypes.includes(course.type)) ||
            (selectedTypes.includes('18 Holes') && course.number_of_holes >= 18)
          );
      // Architect filter
      const matchesArchitect =
        selectedArchitects.length === 0 ||
        selectedArchitects.includes(course.course_architect);
      // Price filter
      const priceValue = parseInt(course.price?.replace(/\D/g, "")) || 0;
      const matchesMinPrice = minPrice === null || priceValue >= minPrice;
      const matchesMaxPrice = maxPrice === null || priceValue <= maxPrice;
      // Ranked only
      const matchesRanked = !rankedOnly || (course.top100_state_rank && course.top100_state_rank !== '');
      // Media filter
      const hasMedia = mediaList && mediaList.some(m => m.course_id === course.course_id);
      const matchesHasMedia = !hasMediaOnly || hasMedia;
      const matchesMediaTypes = selectedMediaTypes.length === 0 ||
        (mediaList && mediaList.some(m => m.course_id === course.course_id && selectedMediaTypes.includes(m.media_type)));
      
      // Top Courses filters
      const matchesTop100All = !top100AllOnly || (course.GD100all_rank && course.GD100all_rank !== '');
      const matchesTop100Public = !top100PublicOnly || (course.GD100pub_rank && course.GD100pub_rank !== '');
      
      // Custom criteria filter (supports dynamic selectedCourseIds or legacy guide keys)
      let matchesCustomCriteria = true;
      if (customCriteria.selectedCourseIds && customCriteria.selectedCourseIds.length > 0) {
        // Dynamic course selection from admin UI
        matchesCustomCriteria = customCriteria.selectedCourseIds.includes(course.course_id);
      } else if (customCriteria.chicagoGuide) {
        // Legacy: Filter to only show courses that are in the Chicago guide
        const chicagoCourseIds = [
          "USA-IL-00546-01", // The Glen Club
          "USA-IL-00095-04", // Cog Hill - Dubsdread
          "USA-IL-00375-01", // The Preserve at Oak Meadows
          "USA-IL-00530-01", // Sunset Valley Golf Club
          "USA-IL-00433-01"  // Ravisloe Country Club
        ];
        matchesCustomCriteria = chicagoCourseIds.includes(course.course_id);
      } else if (customCriteria.sanDiegoGuide) {
        // Legacy: Filter to only show courses that are in the San Diego guide
        const sanDiegoCourseIds = [
          "USA-CA-00032-01", // Aviara Golf Club
          "USA-CA-00715-01", // Torrey Pines - South Course
          "USA-CA-00715-02", // Torrey Pines - North Course
          "USA-CA-00209-01", // Encinitas Ranch Golf Course
          "USA-CA-00902-01"  // Balboa Park Municipal Golf Club
        ];
        matchesCustomCriteria = sanDiegoCourseIds.includes(course.course_id);
      }
      
      return matchesType && matchesArchitect && matchesMinPrice && matchesMaxPrice && matchesRanked && matchesHasMedia && matchesMediaTypes && matchesTop100All && matchesTop100Public && matchesCustomCriteria;
    });
  }, [golfCourses, selectedTypes, selectedArchitects, minPrice, maxPrice, rankedOnly, sortOrder, mediaList, hasMediaOnly, selectedMediaTypes, customCriteria, top100AllOnly, top100PublicOnly]);

  // Helper to check if a course has media
  function courseHasMedia(course) {
    return mediaList && mediaList.some(m => m.course_id === course.course_id);
  }

  // Helper: get top N prioritized courses in bounds
  function getTopCoursesInBounds(bounds, center, N) {
    // Add a small buffer to bounds to reduce edge popping
    const buffer = 0.01;
    const minLat = bounds.getSouth() - buffer;
    const maxLat = bounds.getNorth() + buffer;
    const minLng = bounds.getWest() - buffer;
    const maxLng = bounds.getEast() + buffer;

    // Only filter by bounds since filteredCourses is already filtered by other criteria
    const filtered = filteredCourses.filter((course) => {
      return course.lat >= minLat &&
             course.lat <= maxLat &&
             course.lng >= minLng &&
             course.lng <= maxLng;
    });

    const group1 = [], group2 = [];
    filtered.forEach(course => {
      const hasNationalRank = !!(course.GD100all_rank && course.GD100all_rank !== '');
      const hasPublicRank = !!(course.GD100pub_rank && course.GD100pub_rank !== '');
      const hasStateRank = !!(course.top100_state_rank && course.top100_state_rank !== '');
      const isRanked = hasNationalRank || hasPublicRank || hasStateRank;
      if (isRanked) {
        group1.push(course);
      } else {
        group2.push(course);
      }
    });
    
    // Sort ranked courses by priority: Public > National > State > Price
    group1.sort((a, b) => {
      // First priority: GD100pub_rank (Public ranking)
      const aPublicRank = parseInt(a.GD100pub_rank) || 999;
      const bPublicRank = parseInt(b.GD100pub_rank) || 999;
      if (aPublicRank !== bPublicRank) return aPublicRank - bPublicRank;
      
      // Second priority: GD100all_rank (National ranking)
      const aNationalRank = parseInt(a.GD100all_rank) || 999;
      const bNationalRank = parseInt(b.GD100all_rank) || 999;
      if (aNationalRank !== bNationalRank) return aNationalRank - bNationalRank;
      
      // Third priority: top100_state_rank (State ranking)
      const aStateRank = parseInt(a.top100_state_rank) || 999;
      const bStateRank = parseInt(b.top100_state_rank) || 999;
      if (aStateRank !== bStateRank) return aStateRank - bStateRank;
      
      // Fourth priority: Price (higher price first)
      const aPrice = (a.price && parseInt(a.price.replace(/\D/g, ""))) || 0;
      const bPrice = (b.price && parseInt(b.price.replace(/\D/g, ""))) || 0;
      return bPrice - aPrice;
    });
    
    // Sort unranked courses by distance from center
    group2.sort((a, b) => {
      const distA = Math.sqrt(Math.pow(a.lat - center.lat, 2) + Math.pow(a.lng - center.lng, 2));
      const distB = Math.sqrt(Math.pow(b.lat - center.lat, 2) + Math.pow(b.lng - center.lng, 2));
      return distA - distB;
    });
    
    const pinLimit = 500;
    const visible = [];
    for (const course of group1) { if (visible.length < pinLimit) visible.push(course); else break; }
    for (const course of group2) { if (visible.length < pinLimit) visible.push(course); else break; }
    return visible.length < pinLimit ? visible : visible.slice(0, pinLimit);
  }

  // Helper: group features by proximity
  function groupByProximity(features, threshold = 0.0005) {
    const groups = [];
    features.forEach(f => {
      let group = groups.find(g => g.some(o => Math.abs(o.geometry.coordinates[0] - f.geometry.coordinates[0]) < threshold && Math.abs(o.geometry.coordinates[1] - f.geometry.coordinates[1]) < threshold));
      if (!group) {
        group = [];
        groups.push(group);
      }
      group.push(f);
    });
    return groups;
  }

  // Improved spiderfy: always spiderfy groups with >1 feature at high zoom
  function improvedSpiderfy(features, zoom) {
    if (zoom < 15) return features;
    const groups = groupByProximity(features);
    let spiderfied = [];
    groups.forEach(group => {
      if (group.length === 1) {
        spiderfied.push(group[0]);
      } else {
        const angleStep = (2 * Math.PI) / group.length;
        const radius = 0.0008;
        group.forEach((f, i) => {
          const angle = i * angleStep;
          const dx = Math.cos(angle) * radius;
          const dy = Math.sin(angle) * radius;
          spiderfied.push({
            ...f,
            geometry: {
              ...f.geometry,
              coordinates: [
                f.geometry.coordinates[0] + dx,
                f.geometry.coordinates[1] + dy
              ]
            },
            properties: {
              ...f.properties,
              spiderfied: true,
              spiderfiedIndex: i,
              spiderfiedCount: group.length
            }
          });
        });
      }
    });
    return spiderfied;
  }

  // Memoized updateClusters to always use latest filteredCourses
  const updateClusters = useCallback(() => {
    if (!superclusterRef.current || !mapInstance.current) {
      return;
    }
    const bounds = mapInstance.current.getBounds();
    const zoom = Math.round(mapInstance.current.getZoom());
    const center = mapInstance.current.getCenter();
    
    // Update cluster radius based on current zoom
    if (superclusterRef.current.options.initial) {
      const getClusterRadius = superclusterRef.current.options.initial().getClusterRadius;
      superclusterRef.current.options.radius = getClusterRadius(zoom);
    }
    
    const topCourses = getTopCoursesInBounds(bounds, { lat: center.lat, lng: center.lng }, MAX_CLUSTER_COURSES);
    
    if (zoom >= 16) {
      // At very high zoom, show all as singles (no clustering)
      const features = topCourses.map(course => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [course.lng, course.lat] },
        properties: { ...course, cluster: false }
      }));
      setClusters(features);
      return;
    }
    
    const features = topCourses.map(course => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [course.lng, course.lat] },
      properties: { ...course }
    }));
    
    superclusterRef.current.load(features);
    let clusterResults = superclusterRef.current.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      zoom
    );

    // First, separate clusters and points
    const clusterPoints = clusterResults.filter(f => f.properties.cluster);
    const singlePoints = clusterResults.filter(f => !f.properties.cluster);
    
    // Only filter singles if we're at a lower zoom level
    let filteredSingles = singlePoints;
    if (zoom < 12) { // Only apply distance filtering when zoomed out
      const threshold = 0.05 * (16 - zoom) / 13; // Dynamic threshold that reduces as you zoom in
      filteredSingles = singlePoints.filter(single => {
        const [singleLng, singleLat] = single.geometry.coordinates;
        // Check if this single point is too close to any cluster
        return !clusterPoints.some(cluster => {
          const [clusterLng, clusterLat] = cluster.geometry.coordinates;
          const distance = Math.sqrt(
            Math.pow(clusterLng - singleLng, 2) + 
            Math.pow(clusterLat - singleLat, 2)
          );
          return distance < threshold;
        });
      });
    }
    
    // Combine filtered singles with clusters
    clusterResults = [...clusterPoints, ...filteredSingles];
    
    // Apply spiderfy at high zoom levels
    if (zoom >= 15) {
      clusterResults = improvedSpiderfy(clusterResults, zoom);
    }
    
    setClusters(clusterResults);
  }, [filteredCourses]);

  // 1. Create the map instance only once
  useEffect(() => {
    let map;
    let isMounted = true;
    withIdentityPoolId(identityPoolId).then(authHelper => {
      if (!isMounted || !mapRef.current) return;
      map = new maplibregl.Map({
        container: mapRef.current,
        center: [-98, 39],
        zoom: isMobile ? 2.2 : 3.0,
        style: `https://maps.geo.${region}.amazonaws.com/maps/v0/maps/${mapName}/style-descriptor`,
        ...authHelper.getMapAuthenticationOptions(),
      });
      mapInstance.current = map;

      // Add load event listener to create markers immediately after map loads
      map.on('load', () => {
        if (!isMounted) return;
        setMapLoaded(true);
      });
    });
    return () => {
      isMounted = false;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []); // Changed dependency to empty array to run only once

  // Initialize Supercluster and add event listeners when map is loaded
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current) return;

    const map = mapInstance.current;
    
    // Initialize supercluster
    const getClusterRadius = (zoom) => {
        const desktopRadius = 
          zoom >= 14 ? 20 :
          zoom >= 12 ? 30 :
          zoom >= 10 ? 40 :
          zoom >= 8 ? 50 :
          zoom >= 6 ? 60 :
          70;

        return isMobile ? desktopRadius * 0.7 : desktopRadius;
    };

    superclusterRef.current = new Supercluster({
        radius: getClusterRadius(map.getZoom()),
        maxZoom: 16,
        minPoints: 2,
        initial: () => ({
            getClusterRadius: getClusterRadius
        })
    });

    // Add event listeners
    map.on("moveend", updateClusters);
    map.on("zoomend", updateClusters);

    // Initial cluster update
    updateClusters();

    // Cleanup
    return () => {
        if (map) {
            map.off("moveend", updateClusters);
            map.off("zoomend", updateClusters);
        }
    };
  }, [mapLoaded, updateClusters]);

  // Listen for map move/zoom to update clusters
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current) return;
    const map = mapInstance.current;
    map.on("moveend", updateClusters);
    map.on("zoomend", updateClusters);
    return () => {
      map.off("moveend", updateClusters);
      map.off("zoomend", updateClusters);
    };
  }, [mapLoaded, updateClusters]);

  // Update clusters when any filter changes
  useEffect(() => {
    if (mapLoaded) {
      updateClusters();
    }
  }, [filteredCourses, mapLoaded]); // Depend on filteredCourses directly

  // 2. Manage markers in a separate effect
  useEffect(() => {
    const map = mapInstance.current;
    if (!mapLoaded) return;
    if (!map) return;
    if (!golfCourses || golfCourses.length === 0) return;
    if (!clusters || clusters.length === 0) return;
    
    // Remove all existing markers
    if (map.getContainer()) {
      const markers = document.getElementsByClassName('maplibregl-marker');
      while (markers.length > 0) {
        markers[0].remove();
      }
    }
    markersRef.current = {};
    
    const zoom = Math.round(map.getZoom());
    
    // Render markers from clusters
    clusters.forEach(cluster => {
      const [lng, lat] = cluster.geometry.coordinates;
      let el;
      
      if (cluster.properties.cluster) {
        // Cluster marker
        el = document.createElement("div");
        el.style.background = "#222";
        el.style.color = "#fff";
        el.style.borderRadius = "50%";
        el.style.width = "24px";
        el.style.height = "24px";
        el.style.display = "flex";
        el.style.alignItems = "center";
        el.style.justifyContent = "center";
        el.style.fontWeight = "bold";
        el.style.fontSize = "11px";
        el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
        el.innerText = cluster.properties.point_count_abbreviated;
        el.style.cursor = "pointer";
        el.style.border = "2px solid #fff";
        
        // Add hover effect for green circle only
        el.addEventListener("mouseenter", () => {
          el.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
        });
        el.addEventListener("mouseleave", () => {
          el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
        });

        el.addEventListener("click", () => {
          const expansionZoom = Math.min(
            superclusterRef.current.getClusterExpansionZoom(cluster.id),
            18
          );
          
          map.flyTo({
            center: [lng, lat],
            zoom: expansionZoom,
            essential: true
          });
        });
      } else {
        // Single course marker
        el = document.createElement("div");
        const photoUrl = cluster.properties.photo_url || cluster.properties.image || null;
        if (photoUrl) {
          el.style.width = "36px";
          el.style.height = "36px";
          el.style.borderRadius = "50%";
          el.style.overflow = "hidden";
          el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
          el.style.border = "2px solid #fff";
          el.innerHTML = `<img src='${photoUrl}' alt='' style='width:100%;height:100%;object-fit:cover;border-radius:50%;' />`;
        } else {
          el.style.width = "18px";
          el.style.height = "18px";
          el.style.background = "#222";
          el.style.borderRadius = "50%";
          el.style.border = "2px solid #fff";
          el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
        }
        el.style.cursor = "pointer";
        
        // Add special styling for spiderfied markers
        if (cluster.properties.spiderfied) {
          el.style.background = "#1D665D";
          el.style.border = "2px solid #fff";
          el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
          el.dataset.spiderfied = 'true';
        }
        
        el.addEventListener("mouseenter", (e) => {
          if (justClickedRef.current) return;
          if (tooltipInfo && tooltipInfo.sticky) return;
          el.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
          if (tooltipTimeoutRef.current) { clearTimeout(tooltipTimeoutRef.current); }
          const rect = el.getBoundingClientRect();
          const mapContainer = map.getContainer();
          const mapRect = mapContainer.getBoundingClientRect();
          tooltipTimeoutRef.current = setTimeout(() => {
            setTooltipInfo({
              course: cluster.properties,
              position: { x: rect.left - mapRect.left + rect.width / 2, y: rect.top - mapRect.top - 4 },
              sticky: false
            });
          }, 200);
        });

        el.addEventListener("mouseleave", () => {
          if (!selectedCourse || selectedCourse.name !== cluster.properties.name) {
            el.style.boxShadow = photoUrl ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.18)";
          }
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          // Always hide non-sticky tooltips when mouse leaves
          setTooltipInfo(currentTooltipInfo => {
            if (currentTooltipInfo && !currentTooltipInfo.sticky) {
              return null;
            }
            return currentTooltipInfo;
          });
        });

        el.addEventListener("click", (e) => {
          e.stopPropagation();

          justClickedRef.current = true;
          setTimeout(() => {
            justClickedRef.current = false;
          }, 300);

          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          if (tooltipInfo && !tooltipInfo.sticky) {
            setTooltipInfo(null);
          }

          const clickedCourse = cluster.properties;

          if (isMobile) {
            const rect = el.getBoundingClientRect();
            const mapContainer = map.getContainer();
            const mapRect = mapContainer.getBoundingClientRect();
            setTooltipInfo({
              course: clickedCourse,
              position: {
                x: rect.left - mapRect.left + rect.width / 2,
                y: rect.top - mapRect.top - 4
              },
              sticky: true
            });
          } else {
            setSelectedCourse(clickedCourse);
          }
        });
      }
      
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);
      
      // Use cluster.id for clusters, cluster.properties.course_id for individual markers
      const key = cluster.properties.cluster ? cluster.id : cluster.properties.course_id;
      markersRef.current[key] = { marker, element: el };
    });
    
    // Highlight selected marker
    if (selectedCourse && markersRef.current[selectedCourse.course_id]) {
      markersRef.current[selectedCourse.course_id].element.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
    }
    
    // Helper to get visible courses in bounds
    const getVisibleCourses = () => {
      const bounds = map.getBounds();
      return filteredCourses.filter(course =>
        course.lng >= bounds.getWest() &&
        course.lng <= bounds.getEast() &&
        course.lat >= bounds.getSouth() &&
        course.lat <= bounds.getNorth()
      );
    };
    
    // Update visible courses
    if (onVisibleCoursesChange) {
      onVisibleCoursesChange(getVisibleCourses());
    }
    
    return () => {
      if (map.getContainer()) {
        const markers = document.getElementsByClassName('maplibregl-marker');
        while (markers.length > 0) {
          markers[0].remove();
        }
      }
      markersRef.current = {};
    };
  }, [clusters, selectedCourse, onVisibleCoursesChange, golfCourses, mapLoaded]);

  // Highlight marker when selectedCourse changes
  useEffect(() => {
    Object.values(markersRef.current).forEach(({ element }) => {
      // Reset to default style based on whether it's a cluster or individual marker
      const photoUrl = element.querySelector('img')?.src;
      const isSpiderfied = element.dataset.spiderfied === 'true';
      if (isSpiderfied) {
        element.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
      } else {
        element.style.boxShadow = photoUrl ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.18)";
      }
    });
    if (selectedCourse && markersRef.current[selectedCourse.course_id]) {
      markersRef.current[selectedCourse.course_id].element.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
    }
  }, [selectedCourse]);

  // Highlight marker when hoveredCourseId changes
  useEffect(() => {
    Object.values(markersRef.current).forEach(({ element }) => {
      // Reset to default style based on whether it's a cluster or individual marker
      const photoUrl = element.querySelector('img')?.src;
      const isSpiderfied = element.dataset.spiderfied === 'true';
      if (!selectedCourse || selectedCourse.course_id !== hoveredCourseId) {
        if (isSpiderfied) {
          element.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
        } else {
          element.style.boxShadow = photoUrl ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.18)";
        }
      }
    });
    if (hoveredCourseId && markersRef.current[hoveredCourseId]) {
      markersRef.current[hoveredCourseId].element.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
    }
  }, [hoveredCourseId, selectedCourse]);

  // Sidebar rendering
  const coursesToShow = sidebarCourses.length > 0 ? sidebarCourses : filteredCourses;

  // Add this new effect to close the sticky tooltip when clicking on the map
  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    
    const handleMapClick = () => {
      if (tooltipInfo && tooltipInfo.sticky) {
        setTooltipInfo(null);
      }
    };
    
    map.on('click', handleMapClick);
    
    return () => {
      if (map) {
        map.off('click', handleMapClick);
      }
    };
  }, [tooltipInfo]);

  // Helper function to find which cluster contains a specific course
  const findClusterForCourse = (course) => {
    if (!clusters || !mapInstance.current) {
      return null;
    }
    
    const zoom = Math.round(mapInstance.current.getZoom());
    // Use appropriate thresholds for specialized map clustering
    const threshold = zoom >= 15 ? 0.1 : 0.5;
    
    // First, look for individual course markers (non-clusters)
    const individualCluster = clusters.find(c => 
      !c.properties.cluster && c.properties.name === course.name
    );
    if (individualCluster) {
      return individualCluster;
    }
    
    // For clusters, find the closest one within threshold
    let closestCluster = null;
    let closestDistance = Infinity;
    
    clusters.forEach(c => {
      if (!c.properties.cluster) return;
      
      const [clusterLng, clusterLat] = c.geometry.coordinates;
      const distance = Math.sqrt(
        Math.pow(clusterLng - course.lng, 2) + 
        Math.pow(clusterLat - course.lat, 2)
      );
      
      if (distance < threshold && distance < closestDistance) {
        closestDistance = distance;
        closestCluster = c;
      }
    });
    
    return closestCluster;
  };

  // Helper function to get marker element for a course
  const getMarkerElementForCourse = (course) => {
    const cluster = findClusterForCourse(course);
    if (!cluster) return null;
    
    const key = cluster.properties.cluster ? cluster.id : (cluster.properties.course_id || cluster.properties.name);
    const markerRef = markersRef.current[key];
    return markerRef ? markerRef.element : null;
  };

  // This effect ensures the tooltip stays anchored to its marker during map movement.
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !tooltipInfo) return;

    const updatePosition = () => {
      setTooltipInfo(currentTooltipInfo => {
        if (!currentTooltipInfo) return null;

        const markerElement = getMarkerElementForCourse(currentTooltipInfo.course);
        if (markerElement) {
          const rect = markerElement.getBoundingClientRect();
          const mapContainer = map.getContainer();
          const mapRect = mapContainer.getBoundingClientRect();

          const newPosition = {
            x: rect.left - mapRect.left + rect.width / 2,
            y: rect.top - mapRect.top - 4,
          };
          
          return { ...currentTooltipInfo, position: newPosition };
        }
        
        return null;
      });
    };

    map.on('move', updatePosition);
    map.on('zoom', updatePosition);

    return () => {
      if (map) {
        map.off('move', updatePosition);
        map.off('zoom', updatePosition);
      }
    };
  }, [tooltipInfo]);

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', position: 'relative' }}>
      <div ref={mapRef} style={{ width: showSidebar && !isMobile ? '65%' : '100%', height: '100%', minHeight: 400 }} />
      <MarkerTooltip 
        tooltipInfo={tooltipInfo}
        isMobile={isMobile}
        onTitleClick={setSelectedCourse}
      />
      {showSidebar && !isMobile && (
        <div style={{ width: '35%', height: '100%', overflowY: 'auto', background: '#fafafa', borderLeft: '1px solid #eee', padding: '1rem 0.5rem' }}>
          <h2 style={{ padding: '0 1rem', margin: '0 0 1rem 0' }}>Courses in this Article</h2>
          <div style={{}}>
            {coursesToShow.map((course, index) => (
              <div
                key={course.course_id || index}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #eee',
                  background: selectedCourse?.course_id === course.course_id ? '#e4f8ec' : '#fafafa',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={() => {
                  const markerElement = getMarkerElementForCourse(course);
                  if (markerElement) {
                    markerElement.style.boxShadow = "0 0 0 4px #1D665D, 0 2px 8px rgba(0,0,0,0.18)";
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                      tooltipTimeoutRef.current = null;
                    }
                    
                    // Position hover card over the cluster that contains this course
                    const rect = markerElement.getBoundingClientRect();
                    const mapContainer = mapInstance.current.getContainer();
                    const mapRect = mapContainer.getBoundingClientRect();
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setTooltipInfo({
                        course: course,
                        position: {
                          x: rect.left - mapRect.left + rect.width / 2,
                          y: rect.top - mapRect.top - 4
                        },
                        sticky: false
                      });
                    }, 200);
                  }
                }}
                onMouseLeave={() => {
                  const markerElement = getMarkerElementForCourse(course);
                  if (markerElement && (!selectedCourse || selectedCourse.course_id !== course.course_id)) {
                    markerElement.style.boxShadow = "0 2px 8px rgba(0,0,0,0.18)";
                  }
                  // Always clear any pending tooltip timeout when leaving sidebar course
                  if (tooltipTimeoutRef.current) {
                    clearTimeout(tooltipTimeoutRef.current);
                    tooltipTimeoutRef.current = null;
                  }
                  // Add delay to allow moving to tooltip
                  if (tooltipInfo && !tooltipInfo.sticky) {
                    tooltipTimeoutRef.current = setTimeout(() => {
                      setTooltipInfo(null);
                    }, 300);
                  }
                }}
                onClick={() => setSelectedCourse(course)}
              >
                <h3 style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>
                  {course.name}
                  <span style={{ fontWeight: 400, color: '#666', fontSize: 15 }}> at {course.club_name}</span>
                </h3>
                <div style={{ color: '#666', fontSize: 14 }}>{course.city}, {course.state}</div>
                <div style={{ color: '#1D665D', fontWeight: 600, fontSize: 15 }}>{course.price === '$Unk' ? 'Price Unknown' : course.price}</div>
                <button
                  style={{
                    marginTop: 8,
                    background: '#1D665D',
                    color: 'white',
                    border: 'none',
                    padding: '6px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    window.open(`/course/${course.course_id}`, '_blank');
                  }}
                >
                  View Details
                </button>
              </div>
          ))}
          </div>
        </div>
      )}
      {/* Course Info Panel */}
      {selectedCourse && (
        <div style={{
          position: 'absolute',
          top: isMobile ? 8 : 20,
          left: isMobile ? '50%' : undefined,
          right: !isMobile && showSidebar ? '37%' : (!isMobile ? 20 : undefined),
          width: isMobile ? 'calc(100% - 40px)' : 500,
          background: 'white',
          borderRadius: 12,
          padding: isMobile ? '1rem' : '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 1,
          maxHeight: isMobile ? '70vh' : undefined,
          overflowY: isMobile ? 'auto' : undefined,
          marginBottom: isMobile ? 8 : undefined,
          transform: isMobile ? 'translateX(-50%)' : undefined,
        }}>
          <button
            onClick={() => setSelectedCourse && setSelectedCourse(null)}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#888',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1
            }}
          >
            ×
          </button>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>
            {selectedCourse.name}
            <span style={{ fontWeight: 400, color: '#666', fontSize: '0.85em', marginLeft: 8 }}>
              at {selectedCourse.club_name}
            </span>
          </h3>
          <p style={{ margin: '4px 0' }}><strong>Location:</strong> {selectedCourse.city}, {selectedCourse.state}</p>
          <p style={{ margin: '4px 0' }}><strong>Price:</strong> {selectedCourse.price === '$Unk' ? 'Price Unknown' : selectedCourse.price}</p>
          <p style={{ margin: '4px 0' }}><strong>Type:</strong> {selectedCourse.type}</p>
          <button
            onClick={() => window.open(`/course/${selectedCourse.course_id}`, '_blank')}
            style={{
              background: '#1D665D',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              marginTop: '1rem',
              width: '100%'
            }}
          >
            View Full Details
          </button>
        </div>
      )}
    </div>
  );
}

export default SpecializedMap; 